/*
 *
 * AdminPage / Growth / GrowthDashboard
 *
 * Full platform growth view. Backed by GET /api/v3/admin/growth, which returns a
 * FLAT object (verified against Api::V3::Admin::GrowthController#index):
 *   { channel_count, gif_count, image_count, share_count,
 *     twitter_share_count, facebook_share_count, email_share_count,
 *     mms_share_count, gifs_by_month: [{ month, count }],
 *     shares_by_month: [{ month, share_type, count }],
 *     shares_by_type:  [{ share_type, count }] }
 * (NOT the nested *_metrics shape — that openapi drift was corrected.)
 */

import React from "react";
import { Link } from "react-router-dom";

import Loader from "components/Loader";
import { useAdminGrowth } from "../api/queries";
import {
  Wrapper,
  Grid,
  Card,
  Metric,
  MetricLabel,
  Section,
  BreakdownRow,
  BreakdownLabel,
  BreakdownValue,
  SharesLink,
} from "./styledComponents";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

const COUNTS = [
  { key: "channel_count", label: "Channels" },
  { key: "gif_count", label: "GIFs" },
  { key: "image_count", label: "Images" },
  { key: "share_count", label: "Shares" },
];

const SHARE_TYPES = [
  { key: "twitter_share_count", label: "Twitter" },
  { key: "facebook_share_count", label: "Facebook" },
  { key: "email_share_count", label: "Email" },
  { key: "mms_share_count", label: "MMS" },
];

const GrowthDashboard = () => {
  const { data, isLoading, isError } = useAdminGrowth();

  if (isLoading) {
    return (
      <Wrapper>
        <h1>Growth</h1>
        <div data-testid="growth-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  if (isError || !data) {
    return (
      <Wrapper>
        <h1>Growth</h1>
        <p className="admin-growth__error">Couldn’t load growth metrics. Try again.</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <h1>Growth</h1>

      <Grid>
        {COUNTS.map((c) => (
          <Card key={c.key}>
            <Metric>{fmt(data[c.key])}</Metric>
            <MetricLabel>{c.label}</MetricLabel>
          </Card>
        ))}
      </Grid>

      <Section>
        <h2>Shares by type</h2>
        {SHARE_TYPES.map((s) => (
          <BreakdownRow key={s.key}>
            <BreakdownLabel>{s.label}</BreakdownLabel>
            <BreakdownValue>{fmt(data[s.key])}</BreakdownValue>
          </BreakdownRow>
        ))}
      </Section>

      <Section>
        <h2>GIFs by month</h2>
        {(data.gifs_by_month || []).length === 0 ? (
          <BreakdownRow>
            <BreakdownLabel>No data</BreakdownLabel>
          </BreakdownRow>
        ) : (
          data.gifs_by_month.map((row) => (
            <BreakdownRow key={row.month}>
              <BreakdownLabel>{row.month}</BreakdownLabel>
              <BreakdownValue>{fmt(row.count)}</BreakdownValue>
            </BreakdownRow>
          ))
        )}
      </Section>

      <SharesLink as={Link} to="/admin/shares">
        View global shares →
      </SharesLink>
    </Wrapper>
  );
};

export default GrowthDashboard;
